import { useEffect } from "react";

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Column, Row } from "@tanstack/react-table";
import { AnimatePresence } from "motion/react";
import { twJoin, twMerge } from "tailwind-merge";

import { DatasetRow } from "src/api/types";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { Spinner } from "src/base-components/Spinner";
import { useResourceSample } from "src/datasets/api/queries";
import { COLUMN_MENU_CLASS } from "src/datasets/DatasetTable/ColumnMenu";
import { RowUpdateFunction } from "src/datasets/DatasetTable/DatasetEditTable";
import {
  useIsEditingCell,
  useIsSelectedCell,
} from "src/datasets/DatasetTable/stores";
import {
  CellId,
  parseJsonOrReturnAsIs,
  ponToJson,
} from "src/datasets/DatasetTable/utils";
import { DatasetIntegrationNode } from "src/datasets/utils";
import { DropdownItem } from "src/design-system/Dropdown";
import { useEventListener } from "src/hooks/usehooks-ts";
import { useAuthoringContext } from "src/router/routerContextHooks";

type SampleIntegrationResponseCellProps = {
  onChange: RowUpdateFunction;
  row: Row<DatasetRow>;
  column: Column<DatasetRow>;
  integrationNode: DatasetIntegrationNode;
  disabled: boolean;
  cellId: CellId;
};

export const SampleIntegrationResponseCell: React.FC<
  SampleIntegrationResponseCellProps
> = ({ integrationNode, row, column, onChange, disabled, cellId }) => {
  const selected = useIsSelectedCell(cellId);
  const editing = useIsEditingCell(cellId);

  const { workspace } = useAuthoringContext();
  const resourceSample = useResourceSample({
    baseUrl: workspace.base_url!,
    integrationNode,
    enabled: false,
  });

  useEffect(() => {
    if (editing && !resourceSample.data) resourceSample.refetch();
  }, [editing, resourceSample]);

  useEventListener("paste", (e) => {
    if (selected) {
      const clipboardData = e.clipboardData?.getData("text") ?? "";
      // If this is a PON value, then convert it to JSON
      // Otherwise, it will return the same string
      const value = ponToJson(clipboardData);
      onChange(row, column.id, parseJsonOrReturnAsIs(value));
    }
  });

  return (
    <Menu as="div" className="ml-auto h-full">
      <MenuButton
        className={twMerge(
          "h-full w-full rounded-none font-inter-normal-12px text-gray-500",
          "flex items-center bg-yellow-50 px-1.5",
          disabled &&
            "cursor-default outline outline-1 outline-gray-100 group-[:not(.is-empty)]:bg-gray-50",
          selected ? "outline outline-1 outline-indigo-500" : "outline-0",
          !editing && "select-none",
        )}
        data-loc="sample-integration-response-button"
        onClick={(e) => e.preventDefault()}
      >
        {disabled ? "" : "Enter response"}
      </MenuButton>
      <AnimatePresence initial={false}>
        {editing && (
          <MenuItems
            anchor={{
              to: "bottom start",
              gap: 4,
              offset: -4,
            }}
            as={MotionDropdownPanel}
            className={twJoin(
              "min-h-[136px] overflow-y-auto py-2",
              COLUMN_MENU_CLASS,
            )}
            static
          >
            <div className="cursor-default select-none px-4 py-2.5 font-inter-normal-13px text-gray-800">
              Sample Responses
            </div>
            {resourceSample.data ? (
              resourceSample.data.map((sample) => (
                <MenuItem
                  key={sample.name}
                  as={DropdownItem}
                  className="text-gray-600"
                  data-loc={`sample-response-${sample.name}`}
                  onClick={() => onChange(row, column.id, sample.sample)}
                >
                  {sample.name}
                </MenuItem>
              ))
            ) : (
              <Spinner />
            )}
          </MenuItems>
        )}
      </AnimatePresence>
    </Menu>
  );
};
